/**
 * @file BulkBillReview.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description Bulk Add Bills (2026-10-12): the review step — the bills
 *              chosen for review, one at a time, in the normal Add Bill screen
 *              (CreateBill, given the batch item): opened on the item's own
 *              pages and what was read from them, saved by its own Save (the
 *              normal save, made once per item). A strip above says which bill
 *              of how many, why it needs review, and offers Skip and Back to
 *              summary. A credit opens in Add Supplier Credit's mode; Replace
 *              opens the existing bill in Edit Bill with this document put over
 *              it; unreadable pages open blank beside their PDF, to type in.
 */

import React, { useCallback } from 'react';
import CreateBill from './CreateBill';
import { useSlideOutButton } from './SlideOutButton';
import { sourceText, PLAN_STATUS } from '../utils/bulkBillPlan';
import { API_URL } from '../config';
import './BulkAddBills.css';

/** The item's pages as a File, as if the user had chosen it in Add Bill. */
export async function loadItemFile(item) {
  const res = await fetch(`${API_URL}/api/bulk-bills/items/${item.id}/pdf`, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.error || `The pages could not be loaded (${res.status})`);
  }
  const blob = await res.blob();
  const name = decodeURIComponent(res.headers.get('X-File-Name') || '') || item.file_name || 'bill.pdf';
  return new File([blob], name, { type: 'application/pdf' });
}

function BulkBillReview({ queue, current, plan, position, onSaved, onSkip, onBack, onPick, busy = false }) {
  useSlideOutButton([current?.id, busy]);
  const loadFile = useCallback(() => loadItemFile(current), [current]);
  if (!current) return null;
  const mode = current.dup_choice === 'replace' && current.existing_bill_id ? 'replace' : 'new';
  const reasons = (plan?.reasons || []).filter(Boolean);
  const isBill = current.kind === 'bill' || current.kind === 'credit';

  return (
    <div className="bulk-bills-review">
      <div className="bulk-bills-review-strip">
        <div className="bulk-bills-review-where">
          <strong>Reviewing {position} of {queue.length}</strong>
          <span className="muted"> · {sourceText(current)}</span>
          {mode === 'replace' && <span className="bulk-tag">Replacing an existing bill</span>}
          {current.kind === 'credit' && <span className="bulk-tag">Supplier credit</span>}
          {plan?.status && <span className="bulk-tag">{PLAN_STATUS[plan.status] || plan.status}</span>}
        </div>
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onBack} disabled={busy}
              title="Back to the summary — this bill stays on the review list">
              <div className="icon-wrapper"><span className="material-symbols-rounded">table_rows</span></div>
              <span className="btn-label">Back to summary</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={() => onPick(-1)} disabled={busy || position <= 1}
              title={position <= 1 ? 'This is the first on the review list' : 'The bill before this one'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">navigate_before</span></div>
              <span className="btn-label">Previous</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={() => onPick(1)} disabled={busy || position >= queue.length}
              title={position >= queue.length ? 'This is the last on the review list' : 'Leave this one for now — the next bill'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">navigate_next</span></div>
              <span className="btn-label">Next</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={onSkip} disabled={busy} data-write-action
              title={isBill ? 'Do not save this bill — it is marked skipped (Undo on the summary brings it back)' : 'Discard these pages'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">{isBill ? 'skip_next' : 'delete'}</span></div>
              <span className="btn-label">{isBill ? 'Skip' : 'Discard'}</span>
            </button>
          </div>
        </div>
      </div>
      {reasons.length > 0 && (
        <div className="bulk-bills-callout warn">
          <span className="material-symbols-rounded">rate_review</span>
          <div>
            <strong>Check before saving:</strong>
            <ul>{reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
          </div>
        </div>
      )}
      <CreateBill
        key={`${current.id}:${mode}`}
        bulk={{
          item: current,
          loadFile,
          mode,
          existingBillId: current.existing_bill_id || null,
          onSaved,
          onCancel: onBack,
        }}
      />
    </div>
  );
}

export default BulkBillReview;
